import React, { useEffect, useState } from 'react';
import { AlertTriangle, Download, ExternalLink, MessageCircle } from 'lucide-react';
import { APP_VERSION_CODE, APP_VERSION_NAME } from '@/version';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SUPPORT_WHATSAPP = (process.env.REACT_APP_WHATSAPP_SUPPORT || '+18297091155').replace(/[^\d]/g, '');
const SUPPORT_WHATSAPP_DISPLAY = '+1 (829) 709-1155';

// Detect Android — covers TWA from Play Store, PWA installed on Android, and
// regular Android Chrome/Firefox/Samsung Internet. iOS/desktop are never affected.
const isAndroidClient = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = (navigator.userAgent || '').toLowerCase();
  return ua.includes('android');
};

// Detect Trusted Web Activity OR native WebView shell (our APK).
//
// The RecargameYa Android APK from Play Store uses a NATIVE WebView shell
// (`WebAppActivity` in Kotlin) with JS bridges — NOT a pure TWA anymore.
// So `document.referrer` typically won't start with `android-app://`; that
// only happens on the legacy TWA `LauncherActivity` path or when opened via
// deep-link from Chrome Custom Tabs.
//
// Signals we treat as "user is inside our official APK":
//   1. `window.AndroidPrinter`  → exposed by BluetoothPrinterBridge
//   2. `window.AndroidNotifier` → exposed by NotificationBridge
//   3. `document.referrer` starts with `android-app://com.recargameya.app`
//
// Any of the above → don't block the user with the force-update modal;
// they physically installed the APK from Play Store. Bundle staleness is
// handled by Service Worker v68 refreshing in background.
const isTrustedWebActivity = () => {
  if (typeof window !== 'undefined') {
    if (typeof window.AndroidPrinter !== 'undefined') return true;
    if (typeof window.AndroidNotifier !== 'undefined') return true;
  }
  if (typeof document !== 'undefined') {
    const ref = document.referrer || '';
    if (ref.startsWith('android-app://com.recargameya.app')) return true;
  }
  return false;
};

const ForceUpdateGate = ({ children }) => {
  const [state, setState] = useState({ checked: false, data: null });

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!isAndroidClient()) {
        if (!cancelled) setState({ checked: true, data: null });
        return;
      }
      try {
        const twa = isTrustedWebActivity() ? '1' : '0';
        const url = `${BACKEND_URL}/api/app/version-check?version=${APP_VERSION_CODE}&platform=android&is_twa=${twa}`;
        const resp = await fetch(url, { cache: 'no-store' });
        if (!resp.ok) throw new Error('version-check failed');
        const data = await resp.json();
        if (!cancelled) setState({ checked: true, data });
      } catch (_err) {
        // Network or backend error → fail open (don't lock users out).
        if (!cancelled) setState({ checked: true, data: null });
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  // While we check, render the app normally to avoid a flash of empty screen.
  if (!state.checked) return children;

  const data = state.data;
  if (!data || !data.needs_update) return children;

  const action = data.action || 'play_store';
  const isReloadMode = action === 'reload';
  const isInstallMode = action === 'install_apk';
  const titleText = isInstallMode ? 'Instala la app oficial' : 'Actualización requerida';
  const buttonLabel = isReloadMode
    ? 'Actualizar app'
    : isInstallMode
      ? 'Instalar desde Play Store'
      : 'Actualizar ahora';
  const footnote = isReloadMode
    ? 'Al pulsar Actualizar se descargará la última versión.'
    : isInstallMode
      ? 'Te llevaremos a la Play Store para instalar la app.'
      : 'Al pulsar Actualizar se abrirá la Play Store.';

  const handleUpdate = async () => {
    const action = data.action || 'play_store';

    if (action === 'reload') {
      // Hard refresh: unregister all SWs, clear caches, then reload bypassing cache.
      // This is the fix for "users on old cached JS" without needing a new APK.
      try {
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        }
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
      } catch (_e) {
        // best-effort
      }
      // Cache-bust query param + full reload from server.
      const sep = window.location.href.includes('?') ? '&' : '?';
      window.location.href = window.location.pathname + sep + '_rfu=' + Date.now();
      return;
    }

    const url = data.play_store_url || 'https://play.google.com/store/apps/details?id=com.recargameya.app';
    // Use top-level navigation so the Play Store intent fires reliably from a TWA.
    window.location.href = url;
  };

  return (
    <>
      {children}
      <div
        data-testid="force-update-gate"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          background: 'rgba(15, 23, 42, 0.92)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
        }}
      >
        <div
          style={{
            maxWidth: 440,
            width: '100%',
            background: '#FFFFFF',
            borderRadius: 16,
            padding: '28px 22px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #f97316, #ef4444)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 14,
            }}
          >
            <AlertTriangle size={32} color="#fff" />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: '4px 0 6px' }}>
            {titleText}
          </h2>
          <p style={{ color: '#475569', fontSize: 15, lineHeight: 1.5, margin: '0 0 18px' }}>
            {data.message}
          </p>
          {!isInstallMode && (
            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '10px 12px',
                fontSize: 13,
                color: '#475569',
                marginBottom: 18,
                textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Tu versión</span>
                <strong>{APP_VERSION_NAME} ({APP_VERSION_CODE})</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <span>Versión mínima</span>
                <strong>
                  {data.min_version_name ? `${data.min_version_name} (${data.min_version})` : data.min_version}
                </strong>
              </div>
            </div>
          )}
          <button
            data-testid="force-update-action-btn"
            onClick={handleUpdate}
            style={{
              width: '100%',
              background: 'linear-gradient(135deg, #16a34a, #15803d)',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '13px 16px',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              boxShadow: '0 8px 20px rgba(22, 163, 74, 0.35)',
            }}
          >
            <Download size={18} />
            {buttonLabel}
            {!isReloadMode && <ExternalLink size={14} style={{ opacity: 0.85 }} />}
          </button>
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 14 }}>{footnote}</p>

          {/* Soporte por WhatsApp */}
          <div
            style={{
              marginTop: 18,
              paddingTop: 14,
              borderTop: '1px solid #e2e8f0',
            }}
          >
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
              ¿Necesitas ayuda?
            </p>
            <a
              href={`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent('Hola, necesito ayuda con la actualización de la app.')}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="force-update-whatsapp-btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                width: '100%',
                background: '#25D366',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                padding: '11px 14px',
                fontSize: 14,
                fontWeight: 600,
                textDecoration: 'none',
                boxShadow: '0 6px 14px rgba(37, 211, 102, 0.30)',
              }}
            >
              <MessageCircle size={17} />
              Escríbenos por WhatsApp
            </a>
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
              {SUPPORT_WHATSAPP_DISPLAY}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForceUpdateGate;
